import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { Paper, Chip, Box, Fade, Grid, Container, useMediaQuery } from "@mui/material"; 
import { Select } from 'antd';
import { Rating } from "react-simple-star-rating";
import StarRatings from "react-star-ratings";
import { useTheme } from "@mui/material/styles"; 

import CommentBox from "./CommentBox";
import { GetCousesOfInstructror, GetReviewByCourse } from "../../../api";
import { FILE_PATH } from "../../../commonFunctions/FilePaths";
import CalculateTimeAgo from "../../../commonFunctions/CalculateTimeAgo";

const REVIEWS_PER_PAGE = 5;

const Reviews = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); 
  
  const [courseCode, setCourseCode] = useState("all");
  const [cmbCourses, setCmbCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    GetCousesOfInstructror(setCmbCourses);
  }, []);

  useEffect(() => {
    setSelectedCourses(null);
    setPage(1);
    GetReviewByCourse(courseCode, setSelectedCourses);
  }, [courseCode]);

  const handleSelectReviews = (value) => {
    setCourseCode(value);
  };

  // Flatten ALL reviews from ALL courses into one list, each tagged with its course
  const allReviews = selectedCourses
    ? selectedCourses.flatMap((course) =>
        (course.reviewResponses || []).map((review) => ({ ...review, _course: course }))
      ).reverse()
    : [];

  const totalReviews = allReviews.length;
  const totalPages = Math.ceil(totalReviews / REVIEWS_PER_PAGE);
  const pagedReviews = allReviews.slice((page - 1) * REVIEWS_PER_PAGE, page * REVIEWS_PER_PAGE);

  // Single course info (for course header card)
  const singleCourse = selectedCourses && selectedCourses.length === 1 ? selectedCourses[0] : null;

const colors = {
  primary: "#17082d",
  dark: "#2b2d49",
  light: "#e8e2ed",

  bgGradientStart: "#fafafa",
  bgGradientEnd: "#f3eff6",

  text: "#17082d",
  accent: "#2b2d49",
};

  const styles = {
    pageWrapper: {
      background: `linear-gradient(135deg, ${colors.bgGradientStart} 0%, ${colors.bgGradientEnd} 100%)`,
      minHeight: '100vh',
      paddingBottom: '2rem',
      marginLeft: isSmallScreen ? '0px' : '260px', 
      transition: 'margin-left 0.3s ease',
      width: isSmallScreen ? '100%' : 'calc(100% - 260px)', 
    },
    headerCard: {
      background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.dark} 100%)`,
      color: 'white',
      borderRadius: '20px',
      padding: '2rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 40px"#2b2d49",',
    },
    stickySidebar: {
      position: 'sticky',
      top: '20px', 
      height: 'fit-content',
      zIndex: 1,
    },
    filterCard: {
      background: 'white',
      borderRadius: '16px',
      padding: '1.5rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      width: '100%',
    },
    reviewCard: {
      background: 'white',
      borderRadius: '16px',
      marginBottom: '1.5rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      border: '1px solid #ffebee',
    },
    avatar: {
      width: '70px',
      height: '70px',
      borderRadius: '50%',
      objectFit: 'cover',
      border: `3px solid ${colors.primary}`,
      boxShadow: '0 4px 12px" #2b2d49",',
    },
    courseImage: {
      width: '100%',
      height: '200px',
      objectFit: 'cover',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    spinner: {
      display: 'inline-block',
      width: '60px',
      height: '60px',
      border: '4px solid #f3f3f3',
      borderTop: `4px solid ${colors.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
  };

  const handlePageChange = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Pagination bar ────────────────────────────────────────────────────────
  const PaginationBar = () => {
    if (totalPages <= 1) return null;

    const pageNums = Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.min((page - 1) * REVIEWS_PER_PAGE + 1, totalReviews);
    const end = Math.min(page * REVIEWS_PER_PAGE, totalReviews);

    const btnBase = {
      border: `1.5px solid #ffcdd2`, borderRadius: 8,
      background: '#fff', cursor: 'pointer',
      padding: '6px 11px', fontSize: 13, fontWeight: 500,
      transition: 'all 0.15s', minWidth: 36, fontFamily: 'inherit',
    };

    return (
      <Box
        display="flex" alignItems="center" justifyContent="space-between"
        flexWrap="wrap" gap={1}
        sx={{ background: '#fff', borderRadius: '14px', p: '14px 20px', boxShadow: '0 2px 12px rgba(229,57,53,0.07)', border: '1px solid #ffebee' }}
      >
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: 13 }}>
          Showing <b>{start}–{end}</b> of <b>{totalReviews}</b> reviews
        </Typography>

        <Box display="flex" gap={0.5} alignItems="center">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            style={{ ...btnBase, color: page === 1 ? '#ccc' : colors.primary, cursor: page === 1 ? 'default' : 'pointer', background: page === 1 ? '#fafafa' : '#fff' }}
          >‹ Prev</button>

          {pageNums.map((p) => {
            const isActive = p === page;
            const show = isActive || Math.abs(p - page) <= 1 || p === 1 || p === totalPages;
            const showEllipsisBefore = p === page - 2 && page - 2 > 2;
            const showEllipsisAfter  = p === page + 2 && page + 2 < totalPages - 1;

            if (showEllipsisBefore || showEllipsisAfter) {
              return <span key={p} style={{ color: '#ccc', fontSize: 13, padding: '0 2px' }}>…</span>;
            }
            if (!show) return null;

            return (
              <button
                key={p}
                onClick={() => handlePageChange(p)}
                style={{
                  ...btnBase,
                  background: isActive ? colors.primary : '#fff',
                  borderColor: isActive ? colors.primary : '#ffcdd2',
                  color: isActive ? '#fff' : colors.primary,
                  fontWeight: isActive ? 700 : 500,
                }}
              >{p}</button>
            );
          })}

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            style={{ ...btnBase, color: page === totalPages ? '#ccc' : colors.primary, cursor: page === totalPages ? 'default' : 'pointer', background: page === totalPages ? '#fafafa' : '#fff' }}
          >Next ›</button>
        </Box>
      </Box>
    );
  };

  return (
    <Box style={styles.pageWrapper}>
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .review-card-hover:hover { transform: translateY(-3px); box-shadow: 0 8px 30px rgba(0,0,0,0.12) !important; }
        .custom-select .ant-select-selector:hover { border-color: ${colors.primary} !important; }
        .custom-select .ant-select-focused .ant-select-selector { border-color: ${colors.primary} !important; box-shadow: 0 0 0 2px rgba(229,57,53,0.2) !important; }
      `}</style>

      <Container maxWidth="xl" sx={{ paddingTop: '2rem' }}>

        {/* Header */}
        <Fade in timeout={500}>
          <Box style={styles.headerCard}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <Box>
                <Typography variant="h3" fontWeight="700" mb={1} style={{color:'white'}}>
                  <i className="fas fa-comments" style={{ marginRight: '15px' }}></i>
                  Course Reviews
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  View and manage student feedback across all your courses
                </Typography>
              </Box>
              <Chip
                label={`${cmbCourses.length} Courses`}
                sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '1rem', padding: '1.5rem 0.5rem', fontWeight: '600' }}
              />
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={4}>

          {/* Filter Sidebar */}
          <Grid item xs={12} md={4} lg={3}>
            <Box style={styles.stickySidebar}>
              <Fade in timeout={600}>
                <Box style={styles.filterCard}>
                  <Box display="flex" alignItems="center" mb={3}>
                    <i className="fas fa-filter" style={{ color: colors.primary, marginRight: '10px' }}></i>
                    <Typography variant="h6" fontWeight="600">Filter Reviews</Typography>
                  </Box>

                  <Select
                    onChange={handleSelectReviews}
                    placeholder="All Courses"
                    defaultValue="all"
                    size='large'
                    style={{ width: '100%' }}
                    className="custom-select"
                  >
                    <option value={"all"}>📚 All Courses</option>
                    {cmbCourses.map((course, index) => (
                      <option key={index} value={course.code}>📖 {course.title}</option>
                    ))}
                  </Select>

                  <Box mt={4} pt={4} borderTop="2px solid #ffebee">
                    <Typography variant="body2" color="textSecondary" mb={2}>Quick Stats</Typography>
                    {selectedCourses && (
                      <Box style={{
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.dark} 100%)`,
                        color: '#ffffff', padding: '1.5rem', borderRadius: '12px', textAlign: 'center'
                      }}>
                        <Typography variant="h4" fontWeight="700" style={{color:'white'}}>{totalReviews}</Typography>
                        <Typography variant="body2" fontWeight="600">Total Reviews</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Fade>
            </Box>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={8} lg={9}>
            {selectedCourses !== null ? (
              <Fade in timeout={700}>
                <Box>
                  {/* Single-course header card */}
                  {singleCourse && (
                    <Card sx={{ borderRadius: '16px', mb: 3, overflow: 'visible' }}>
                      <CardContent sx={{ p: 4 }}>
                        <Grid container alignItems="center" spacing={3}>
                          <Grid item xs={12} md={4}>
                            <img src={`${FILE_PATH}${singleCourse.courseImg}`} style={styles.courseImage} alt="Course" />
                          </Grid>
                          <Grid item xs={12} md={8}>
                            <Typography variant="h4" fontWeight="700" mb={2}>{singleCourse.courseTitle}</Typography>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                              <Box sx={{ background: colors.primary, color: 'white', px: 2, py: 0.5, borderRadius: '20px', fontWeight: 'bold' }}>
                                {Number.parseFloat(singleCourse.rating || 0).toFixed(1)}
                              </Box>
                              <StarRatings
                                starDimension="25px" starSpacing="2px"
                                rating={singleCourse.rating || 0}
                                starRatedColor={colors.primary} numberOfStars={5}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Review cards for current page */}
                  {totalReviews > 0 ? (
                    <>
                      {pagedReviews.map((review, rIndex) => (
                        <Card key={rIndex} className="review-card-hover" style={styles.reviewCard}>
                          <CardContent style={{ padding: '2rem' }}>
                            <Box display="flex" alignItems="flex-start" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
                              <img
                                src={review.userProfile ? `${FILE_PATH}${review.userProfile}` : "../images/user-profile.png"}
                                style={styles.avatar} alt="User"
                              />
                              <Box flex={1} width="100%">
                                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={0.5}>
                                  <Typography variant="h6" fontWeight="600" color={colors.primary}>{review.fullName}</Typography>
                                  {/* Show course tag when viewing all courses */}
                                  {!singleCourse && review._course?.courseTitle && (
                                    <Chip
                                      label={review._course.courseTitle}
                                      size="small"
                                      sx={{ fontSize: 11, background: '#ffebee', color: colors.dark, fontWeight: 600, borderRadius: '20px' }}
                                    />
                                  )}
                                </Box>
                                <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                                  {CalculateTimeAgo(review.date)}
                                </Typography>
                                <Rating size={20} readonly initialValue={review.rating} fillColor={colors.primary} />
                                <Box mt={2} p={2} bgcolor="#fafafa" borderRadius="12px" borderLeft={`4px solid ${colors.primary}`}>
                                  <Typography variant="body1" style={{ color: '#2d3748' }}>{review.comment}</Typography>
                                </Box>
                                <Box mt={2}>
                                  <CommentBox
                                    setcmbCourses={setCmbCourses}
                                    replies={review.replies}
                                    reviewCode={review.reviewCode}
                                  />
                                </Box>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}

                      {/* Pagination bar */}
                      <PaginationBar />
                    </>
                  ) : (
                    <Paper sx={{ p: 5, textAlign: 'center', borderRadius: '16px', border: '2px dashed #ffcdd2' }} elevation={0}>
                      <i className="fas fa-comments" style={{ fontSize: '48px', color: '#ffcdd2', marginBottom: '1rem' }}></i>
                      <Typography color="textSecondary">No reviews found for this course.</Typography>
                    </Paper>
                  )}
                </Box>
              </Fade>
            ) : (
              <Paper style={{ padding: '3rem', borderRadius: '16px', textAlign: 'center' }}>
                <div style={styles.spinner} />
                <Typography variant="h6" color="textSecondary" mt={2}>Loading Reviews...</Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Reviews;

